(() => {
  const state = document.querySelector('[data-ao-campaign-state]');
  if (state && state.dataset.mode === 'active') {
    const serverNow = Number(state.dataset.now) * 1000;
    const loadedAt = performance.now();
    const end = Number(state.dataset.end) * 1000;
    let expiryTimer;
    const expire = () => {
      window.clearTimeout(expiryTimer);
      const remaining = end - (serverNow + performance.now() - loadedAt);
      if (remaining > 0) {
        expiryTimer = window.setTimeout(expire, Math.min(remaining, 60000));
        return;
      }
      document.querySelectorAll('[data-ao-september-only]').forEach((el) => { el.hidden = true; });
      document.querySelectorAll('[data-ao-september-default]').forEach((el) => { el.hidden = false; });
      state.dataset.mode = '';
    };
    expire();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') expire();
    });
  }

  if (customElements.get('ao-mobile-order')) return;
  customElements.define('ao-mobile-order', class extends HTMLElement {
    connectedCallback() {
      this.root = document.getElementById(this.dataset.rootId);
      this.form = this.root?.querySelector('[data-ao-product-form], ao-home-quick-buy form');
      this.primary = this.form?.querySelector('[type="submit"][name="add"]');
      if (!this.primary) return;
      this.add = this.querySelector('[data-ao-island-add]');
      this.price = this.querySelector('[data-ao-island-price]');
      this.compare = this.querySelector('[data-ao-island-compare]');
      this.sizes = this.querySelector('[data-ao-island-sizes]');
      this.variants = [...this.form.querySelectorAll('[data-ao-variant], [data-ao-home-variant]')];
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.abort = new AbortController();
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
      };
      const options = { signal: this.abort.signal, passive: true };
      ['scroll', 'resize'].forEach((name) => window.addEventListener(name, this.schedule, options));
      ['focusin', 'focusout', 'change', 'visitorConsentCollected', 'shopify:section:load'].forEach((name) => document.addEventListener(name, this.schedule, options));
      window.visualViewport?.addEventListener('resize', this.schedule, options);
      this.intersection = new IntersectionObserver(this.schedule, { threshold: [0, 0.1, 1] });
      this.intersection.observe(this.primary);
      this.observer = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.schedule();
      });
      this.observer.observe(document.body, { subtree: true, childList: true, attributes: true,
        attributeFilter: ['class', 'style', 'open', 'hidden', 'aria-hidden', 'aria-disabled', 'disabled'] });
      this.sizeButtons = this.variants.map((variant) => {
        const button = document.createElement('button');
        const title = variant.dataset.variantTitle || variant.dataset.title || '';
        button.type = 'button';
        button.textContent = title;
        button.setAttribute('aria-label', title);
        button.addEventListener('click', () => {
          if (button.disabled) return;
          variant.checked = true;
          variant.dispatchEvent(new Event('change', { bubbles: true }));
          this.update();
          this.showSizes();
        }, { signal: this.abort.signal });
        this.sizes.append(button);
        return button;
      });
      this.add.addEventListener('click', () => {
        // Delegate to the existing form exactly once. No second cart request or tracking event.
        if (!this.selection()) { this.showSizes(); return; }
        if (this.primary.disabled || this.primary.getAttribute('aria-disabled') === 'true') return;
        this.primary.click();
        this.schedule();
      }, { signal: this.abort.signal });
      this.update();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.intersection?.disconnect();
      this.sizes?.replaceChildren();
      cancelAnimationFrame(this.frame);
    }
    selection() {
      return this.form.querySelector('[data-ao-variant]:checked, [data-ao-home-variant]:checked, [data-ao-single-variant]') ||
        (!this.form.querySelector('input[type="radio"]') ? this.form.querySelector('[name="id"]') : null);
    }
    showSizes() {
      const target = this.form.querySelector('input[type="radio"]:checked:not(:disabled)') || this.form.querySelector('input[type="radio"]:not(:disabled)') || this.primary;
      const fieldset = target.closest('fieldset') || this.form;
      fieldset.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      target.focus({ preventScroll: true });
    }
    visible(element) {
      if (element.hidden || element.closest('[inert], [aria-hidden="true"]')) return false;
      const style = getComputedStyle(element), rect = element.getBoundingClientRect();
      return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0 && rect.width > 0 && rect.height > 0;
    }
    update() {
      const selected = this.selection();
      const price = selected?.dataset.variantPrice || selected?.dataset.price ||
        this.root.querySelector('[data-ao-price], [data-ao-home-price]')?.textContent.trim() || '';
      const available = !!selected && !selected.disabled && selected.dataset.variantAvailable !== 'false' && selected.dataset.available !== 'false';
      const busy = this.primary.disabled || this.primary.getAttribute('aria-disabled') === 'true';
      const label = !selected ? this.add.dataset.chooseLabel : !available ? this.add.dataset.soldLabel : this.add.dataset.addLabel;
      if (this.add.textContent !== label) this.add.textContent = label;
      this.add.disabled = !!selected && (!available || busy);
      if (this.price.textContent !== price) this.price.textContent = price;
      const compare = selected?.dataset.variantCompare || selected?.dataset.compare || '';
      if (this.compare.textContent !== compare) this.compare.textContent = compare;
      this.compare.hidden = !compare;
      this.sizeButtons.forEach((button, index) => {
        const variant = this.variants[index];
        button.setAttribute('aria-pressed', String(variant === selected));
        button.disabled = busy || variant.disabled || variant.dataset.variantAvailable === 'false' || variant.dataset.available === 'false';
      });
      const rect = this.primary.getBoundingClientRect();
      const viewHeight = window.visualViewport?.height || window.innerHeight;
      const primaryVisible = rect.bottom > 0 && rect.top < viewHeight && rect.width > 0;
      const editing = document.activeElement?.matches('input:not([type="radio"]):not([type="checkbox"]), textarea, select, [contenteditable="true"]');
      const keyboard = window.visualViewport && window.innerHeight - window.visualViewport.height > 140;
      const blocked = [...document.querySelectorAll('dialog[open], [role="dialog"], cart-drawer.active, menu-drawer details[open], [data-ao2-drawer]:not([hidden]), .ao-added.is-visible, #shopify-pc__banner, #shopify-pc__prefs, .shopify-pc__banner__dialog')].some((el) => this.visible(el));
      const errors = [...this.root.querySelectorAll('[role="alert"], .product-form__error-message-wrapper:not([hidden])')].some((el) => el.textContent.trim() && this.visible(el));
      this.hidden = !this.mobile.matches || primaryVisible || !!editing || !!keyboard || blocked || errors;
    }
  });
})();
